
// message.js
import axios from "../helpers/axios";
import { chatConstants, messageConstants } from "./constants";
import { applyMyReaction, normalizeReactions } from "../utils/messageReactions";
import { detectMediaDocumentKind } from "../utils/mediaDocument";

const getId = (value) => String(value?._id || value || "");

const createClientMessageId = () => (
    `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const errorText = (error, fallback) => (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    (error?.message && error.message !== "Network Error" ? error.message : "") ||
    fallback
);

/*
 * Исходящие, которые ещё отправляются или не отправились: по номеру
 * сообщения здесь лежат текст, файл и всё остальное. Нужны для «Повторить» —
 * файл не надо выбирать заново. Живут, пока открыта вкладка.
 */
const outbox = new Map();

/**
 * Вид вложения по файлу — так же, как его определит сервер: по расширению.
 * ArtCAM, AutoCAD, CorelDRAW и т. п. браузер называет «картинкой», но это
 * документы (utils/mediaDocument.js).
 */
const detectFileKind = (file) => {
    const kind = detectMediaDocumentKind({
        name: String(file?.name || ""),
        mimeType: String(file?.type || ""),
    });
    if (kind === "image") return "image";
    if (kind === "video") return "video";
    if (kind === "audio") return "voice";
    return "doc";
};

/** Отправляем то, что лежит в outbox под этим номером (первый раз или повторно). */
const uploadOutboxItem = (clientMessageId) => async (dispatch) => {
    const item = outbox.get(clientMessageId);
    if (!item) return null;

    const { chatId, data, file } = item;
    const formData = new FormData();
    formData.append("text", data.text || "");
    formData.append("clientMessageId", clientMessageId);
    // Сообщение ждало (нет связи, повтор) — сервер поставит время, когда его написали.
    formData.append("clientCreatedAt", item.createdAt);
    formData.append("clientSentAt", new Date().toISOString());
    if (data.sender) formData.append("sender", data.sender);
    if (data.messageKind) formData.append("messageKind", data.messageKind);
    if (data.mediaDurationSec != null) formData.append("mediaDurationSec", String(data.mediaDurationSec));
    if (data.replyTo) formData.append("replyTo", JSON.stringify(data.replyTo));
    if (data.albumId) formData.append("albumId", data.albumId);
    if (file) formData.append("file", file, file.name || "file");

    let lastProgress = -1;

    try {
        const res = await axios.post(`/messages/${chatId}`, formData, {
            onUploadProgress: (event) => {
                if (!file || !event?.total) return;
                const progress = Math.round((event.loaded * 100) / event.total);
                if (progress === lastProgress) return;
                lastProgress = progress;
                dispatch({
                    type: "SEND_MESSAGE_OPTIMISTIC_PROGRESS",
                    payload: { clientMessageId, progress },
                });
            },
        });
        const message = res?.data?.newMsg || null;

        if (!message) throw new Error("Сообщение не создано");

        outbox.delete(clientMessageId);
        dispatch({
            type: messageConstants.SEND_MESSAGE_OPTIMISTIC_CONFIRM,
            payload: { clientMessageId, message },
        });
        return message;
    } catch (error) {
        dispatch({
            type: messageConstants.SEND_MESSAGE_OPTIMISTIC_FAILURE,
            payload: {
                clientMessageId,
                error: errorText(error, file ? "Не удалось отправить файл" : "Не удалось отправить сообщение"),
            },
        });
        return null;
    }
};

/**
 * Файл, фото, видео, голосовое или кружок. Сообщение сразу появляется в чате
 * (с превью и процентами загрузки), файлы грузятся параллельно и не мешают
 * писать дальше. Не ушло — у сообщения «Повторить».
 */
export const sendMediaMessage = (chatId, {
    sender = null,
    senderUser = null,
    text = "",
    replyTo = null,
    file = null,
    albumId = "",
    clientMessageId = "",
    messageKind = "",
    mediaDurationSec = null,
} = {}) => async (dispatch) => {
    if (!chatId || !file) return null;

    const id = clientMessageId || createClientMessageId();
    const createdAt = new Date().toISOString();
    const kind = detectFileKind(file);
    // Своё фото, видео и голосовое видно сразу — с компьютера, не дожидаясь сервера.
    const localUrl = ["image", "video", "voice"].includes(kind) && typeof URL !== "undefined"
        ? URL.createObjectURL(file)
        : "";

    outbox.set(id, {
        chatId,
        createdAt,
        file,
        data: { sender, text, replyTo, albumId, messageKind, mediaDurationSec },
    });

    dispatch({
        type: messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD,
        payload: {
            clientMessageId: id,
            message: {
                _id: `local:${id}`,
                __clientMessageId: id,
                __optimistic: true,
                __sendStatus: "sending",
                __uploadProgress: 0,
                __localUrl: localUrl,
                chatId,
                sender: senderUser || sender,
                text: text || "",
                replyTo: replyTo || null,
                readBy: sender ? [sender] : [],
                createdAt,
                hasAttachment: true,
                fileName: file.name || "file",
                fileType: kind,
                fileMime: file.type || "",
                fileSize: file.size || 0,
                messageKind: messageKind || (kind === "voice" ? "voice" : "file"),
                mediaDurationSec: mediaDurationSec ?? null,
                ...(albumId ? { albumId } : {}),
            },
        },
    });

    return dispatch(uploadOutboxItem(id));
};

/** «Повторить» у неотправленного сообщения (текст или файл). */
export const retryFailedMessage = (clientMessageId) => async (dispatch) => {
    const id = String(clientMessageId || "");
    if (!outbox.has(id)) return null;

    dispatch({ type: "SEND_MESSAGE_OPTIMISTIC_RETRY", payload: { clientMessageId: id } });
    return dispatch(uploadOutboxItem(id));
};

/** Убрать неотправленное сообщение из чата. */
export const discardFailedMessage = (clientMessageId) => (dispatch) => {
    const id = String(clientMessageId || "");
    outbox.delete(id);
    dispatch({ type: "SEND_MESSAGE_OPTIMISTIC_DISCARD", payload: { clientMessageId: id } });
};

/** Можно ли повторить отправку (файл ещё в памяти вкладки). */
export const canRetryMessage = (clientMessageId) => outbox.has(String(clientMessageId || ""));

const normalizeTimelineItems = (items = [], chatId) => {
    return items
        .filter(Boolean)
        .map((item) => ({
            ...item,
            chatId: item.chatId || chatId,
        }));
};


export const sendMessage = (chatId, messageData = {}, file = null) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const formData = new FormData();

            formData.append("text", messageData.text || "");

            if (messageData.sender) formData.append("sender", messageData.sender);
            if (messageData.messageKind) formData.append("messageKind", messageData.messageKind);
            if (messageData.mediaDurationSec != null) formData.append("mediaDurationSec", String(messageData.mediaDurationSec));
            if (messageData.replyTo) formData.append("replyTo", JSON.stringify(messageData.replyTo));
            if (messageData.entity) formData.append("entity", JSON.stringify(messageData.entity));
            if (file) formData.append("file", file);

            const res = await axios.post(`/messages/${chatId}`, formData, {
                onUploadProgress: (event) => {
                    if (!file || !event?.total) return;

                    const progress = Math.round((event.loaded * 100) / event.total);
                    dispatch({
                        type: messageConstants.SEND_MESSAGE_PROGRESS,
                        payload: { progress },
                    });
                },
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.SEND_MESSAGE_SUCCESS,
                    payload: { message },
                });


                return message;
            }

            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: "Сообщение не создано" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const sendTextMessage = (
    chatId,
    {
        sender = null,
        senderUser = null,
        text = "",
        replyTo = null,
    } = {}
) => {
    return async (dispatch) => {
        const cleanText = String(text || "").trim();
        if (!chatId || !cleanText) return null;

        const clientMessageId = createClientMessageId();
        const createdAt = new Date().toISOString();
        const optimisticMessage = {
            _id: `local:${clientMessageId}`,
            __clientMessageId: clientMessageId,
            __optimistic: true,
            __sendStatus: "sending",
            chatId,
            sender: senderUser || sender,
            text: cleanText,
            replyTo: replyTo || null,
            readBy: sender ? [sender] : [],
            createdAt,
        };

        // Текст появляется в UI сразу. В отличие от файлов этот action намеренно
        // НЕ включает глобальный state.sending: следующий Enter можно нажать
        // немедленно, пока предыдущий POST ещё находится в полёте.
        dispatch({
            type: messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD,
            payload: {
                clientMessageId,
                message: optimisticMessage,
            },
        });

        // Номер сообщения уходит на сервер: при повторе он тот же, и второе
        // сообщение не появится. Не ушло — «Повторить» возьмёт текст отсюда.
        outbox.set(clientMessageId, {
            chatId,
            createdAt,
            file: null,
            data: { sender, text: cleanText, replyTo },
        });

        return dispatch(uploadOutboxItem(clientMessageId));
    };
};

export const sendFileMessage = (chatId, { sender = null, senderUser = null, text = "", replyTo = null, albumId = "", clientMessageId = "" } = {}, file) => {
    return async (dispatch) => {
        return dispatch(sendMediaMessage(chatId, { sender, senderUser, text, replyTo, file, albumId, clientMessageId }));
    };
};

/** Расширение файла записи по её типу: webm, ogg или m4a. */
export const recordingExtension = (mimeType = "") => {
    const mime = String(mimeType || "").toLowerCase();
    if (mime.includes("ogg")) return "ogg";
    if (mime.includes("mp4") || mime.includes("aac") || mime.includes("m4a")) return mime.startsWith("video/") ? "mp4" : "m4a";
    return "webm";
};

export const sendVoiceMessage = (chatId, { sender = null, senderUser = null, durationMs = 0, replyTo = null } = {}, blob) => {
    return async (dispatch) => {
        if (!blob || !blob.size) return null;

        const mimeType = String(blob.type || "audio/webm").split(";")[0] || "audio/webm";
        const file = new File([blob], `voice-${Date.now()}.${recordingExtension(mimeType)}`, { type: mimeType });
        const mediaDurationSec = Math.max(0, Math.round((Number(durationMs) || 0) / 1000));

        return dispatch(sendMediaMessage(chatId, {
            sender,
            senderUser,
            replyTo,
            file,
            messageKind: "voice",
            mediaDurationSec,
        }));
    };
};

/** Кружок: короткое видео с камеры, в чате — круглое. */
export const sendVideoNoteMessage = (chatId, { sender = null, senderUser = null, durationMs = 0, replyTo = null } = {}, blob) => {
    return async (dispatch) => {
        if (!blob || !blob.size) return null;

        const mimeType = String(blob.type || "video/webm").split(";")[0] || "video/webm";
        const file = new File([blob], `note-${Date.now()}.${recordingExtension(mimeType)}`, { type: mimeType });
        const mediaDurationSec = Math.max(1, Math.round((Number(durationMs) || 0) / 1000));

        return dispatch(sendMediaMessage(chatId, {
            sender,
            senderUser,
            replyTo,
            file,
            messageKind: "video_note",
            mediaDurationSec,
        }));
    };
};

export const shareProductToChat = (chatId, productId) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-product`, { productId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: "Товар не отправлен" } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const shareOrderToChat = (chatId, orderId) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-order`, { orderId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: "Заказ не отправлен" } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const shareEntityToChat = (chatId, {
    type,
    entityId,
    officeId,
    text = "",
} = {}) => {
    return async (dispatch) => {
        if (!chatId || !type || !entityId) return null;

        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-entity`, {
                type,
                entityId,
                officeId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.SEND_MESSAGE_SUCCESS,
                    payload: { message },
                });
                return message;
            }

            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: "Объект не отправлен" },
            });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: {
                    error: error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error.message ||
                        "Не удалось отправить объект",
                },
            });
            return null;
        }
    };
};

export const sharePaymentReceiptToChat = (chatId, { paymentAccountId, receiptId, text = '' } = {}) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-payment-receipt`, {
                paymentAccountId,
                receiptId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: 'Чек не отправлен' } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });
            throw error;
        }
    };
};

export const shareSceneScreenshotToChat = (chatId, screenshotId, text = '') => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-scene-screenshot`, {
                screenshotId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: 'Снимок не отправлен' } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });
            throw error;
        }
    };
};

export const getMessagesByChat = (chatId, options = {}) => {
    return async (dispatch) => {
        const {
            before = null,
            limit = 50,
            append = false,
        } = options;

        const requestType = append
            ? messageConstants.GET_MESSAGES_OLDER_REQUEST
            : messageConstants.GET_MESSAGES_REQUEST;

        const failureType = append
            ? messageConstants.GET_MESSAGES_OLDER_FAILURE
            : messageConstants.GET_MESSAGES_FAILURE;

        dispatch({
            type: requestType,
            payload: { chatId },
        });

        try {
            const params = new URLSearchParams();

            params.set("limit", String(limit));

            if (before) params.set("before", before);

            const res = await axios.get(`/chat/${chatId}/timeline?${params.toString()}`);
            const items = normalizeTimelineItems(res?.data?.items || [], chatId);

            dispatch({
                type: messageConstants.GET_MESSAGES_SUCCESS,
                payload: {
                    chatId,
                    messages: items,
                    nextCursor: res?.data?.nextCursor || null,
                    hasMore: !!res?.data?.nextCursor,
                    append,
                },
            });

            return {
                messages: items,
                nextCursor: res?.data?.nextCursor || null,
                hasMore: !!res?.data?.nextCursor,
            };
        } catch (error) {
            const status = error?.response?.status;

            if (!append && status === 404) {
                try {
                    const fallbackRes = await axios.get(`/messages/getMessagesByChat/${chatId}`);
                    const fallbackMessages = Array.isArray(fallbackRes?.data?.messages)
                        ? fallbackRes.data.messages.map((message) => ({ ...message, chatId: message.chatId || chatId }))
                        : [];

                    dispatch({
                        type: messageConstants.GET_MESSAGES_SUCCESS,
                        payload: {
                            chatId,
                            messages: fallbackMessages,
                            nextCursor: null,
                            hasMore: false,
                            append: false,
                        },
                    });

                    return {
                        messages: fallbackMessages,
                        nextCursor: null,
                        hasMore: false,
                    };
                } catch (fallbackError) {
                    dispatch({
                        type: failureType,
                        payload: { chatId, error: fallbackError?.response?.data?.error || fallbackError.message },
                    });

                    return {
                        messages: [],
                        nextCursor: null,
                        hasMore: false,
                    };
                }
            }

            if (status === 403 || status === 404) {
                dispatch({
                    type: chatConstants.REMOVE_CHAT,
                    payload: { chatId },
                });
            }

            dispatch({
                type: failureType,
                payload: { chatId, error: error?.response?.data?.error || error.message },
            });

            return {
                messages: [],
                nextCursor: null,
                hasMore: false,
            };
        }
    };
};

export const markMessagesAsRead = (chatId, userId = null) => {
    return async (dispatch, getState) => {
        const currentUserId = userId || getState().auth?.user?._id || getState().auth?.user?.id;

        if (!chatId || !currentUserId) return false;

        dispatch({ type: messageConstants.MARK_MESSAGES_AS_READ_REQUEST });

        try {
            const res = await axios.patch("/messages/mark-read", { chatId, userId: currentUserId });

            if (res.status === 200) {
                dispatch({
                    type: messageConstants.MARK_MESSAGES_AS_READ_SUCCESS,
                    payload: { chatId, userId: currentUserId, isSelfRead: true },
                });

                return true;
            }

            dispatch({ type: messageConstants.MARK_MESSAGES_AS_READ_FAILURE });

            return false;
        } catch (error) {
            dispatch({
                type: messageConstants.MARK_MESSAGES_AS_READ_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return false;
        }
    };
};

/**
 * Удалить сообщение. scope: «me» — только у себя (у остальных останется),
 * «all» — у всех участников. Без scope — как раньше (у всех).
 */
export const deleteMessage = (messageId, chatId, source = "user", scope = "") => {
    return async (dispatch) => {
        if (source === "socket") {
            dispatch({ type: messageConstants.DELETE_MESSAGE_SUCCESS, payload: { messageId } });
            return true;
        }

        dispatch({ type: messageConstants.DELETE_MESSAGE_REQUEST, payload: { messageId } });

        try {
            const res = await axios.delete(
                `/messages/${messageId}`,
                scope ? { params: { scope } } : undefined
            );

            if (res.status === 200) {
                dispatch({ type: messageConstants.DELETE_MESSAGE_SUCCESS, payload: { messageId } });

                return true;
            }

            dispatch({ type: messageConstants.DELETE_MESSAGE_FAILURE, payload: { messageId } });

            return false;
        } catch (error) {
            dispatch({
                type: messageConstants.DELETE_MESSAGE_FAILURE,
                payload: { messageId, error: error?.response?.data?.error || error.message },
            });

            return false;
        }
    };
};

export const forwardMessage = (messageId, targetChatId) => {
    return async (dispatch) => {
        dispatch({
            type: messageConstants.FORWARD_MESSAGE_REQUEST,
            payload: { messageId, targetChatId },
        });

        try {
            const res = await axios.post(`/messages/${messageId}/forward`, { targetChatId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.FORWARD_MESSAGE_SUCCESS,
                    payload: { message },
                });


                return message;
            }

            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: { error: "Сообщение не переслано" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const forwardMessagesBulk = (messageIds = [], targetChatIds = []) => {
    return async (dispatch) => {
        const sourceIds = Array.from(new Set((messageIds || []).map(getId).filter(Boolean))).slice(0, 100);
        const targetIds = Array.from(new Set((targetChatIds || []).map(getId).filter(Boolean))).slice(0, 10);

        if (!sourceIds.length || !targetIds.length) return null;

        dispatch({
            type: messageConstants.FORWARD_MESSAGE_REQUEST,
            payload: { messageIds: sourceIds, targetChatIds: targetIds },
        });

        try {
            const res = await axios.post("/messages/forward-bulk", {
                messageIds: sourceIds,
                targetChatIds: targetIds,
            });

            const createdMessages = Array.isArray(res?.data?.messages)
                ? res.data.messages
                : [];

            dispatch({
                type: messageConstants.FORWARD_MESSAGE_SUCCESS,
                payload: {
                    messages: createdMessages,
                    result: res?.data || null,
                },
            });

            return res?.data || null;
        } catch (error) {
            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: {
                    error: error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error.message ||
                        "Не удалось переслать сообщения",
                },
            });

            return null;
        }
    };
};

export const toggleMessageStar = (messageId, on) => {
    return async (dispatch) => {
        try {
            const res = await axios.post(`/messages/${messageId}/star`, { on });

            if (res.status === 200) {
                dispatch({
                    type: messageConstants.RECEIVE_NEW_MESSAGE,
                    payload: {
                        message: {
                            _id: messageId,
                            __starPatch: true,
                            on: !!on,
                            starredBy: res.data?.starredBy || [],
                        },
                    },
                });
            }

            return res.data;
        } catch (_error) {
            return null;
        }
    };
};

export const clearMessages = () => ({ type: messageConstants.CLEAR_MESSAGES });

export const clearAdminMessages = clearMessages;

export const receiveMessage = (message, meId = null) => ({
    type: messageConstants.RECEIVE_NEW_MESSAGE,
    payload: { message, meId },
});

export const receiveAdminMessage = receiveMessage;

export const deleteMessageSocket = (messageId) => ({
    type: messageConstants.DELETE_MESSAGE_SUCCESS,
    payload: { messageId },
});

export const deleteMessageLocal = deleteMessageSocket;

export const markMessagesAsReadSocket = (chatId, userId, currentUserId = null) => ({
    type: messageConstants.MARK_MESSAGES_AS_READ_SUCCESS,
    payload: { chatId, userId, isSelfRead: getId(userId) === getId(currentUserId) },
});


/** Same messageUpdated event is used for editing, reactions and voice transcripts. */
export const receiveMessageUpdate = (message) => ({
    type: 'MESSAGE_UPDATED_RECEIVED',
    payload: { message },
});

export const toggleMessageReaction = (messageId, emoji) => async (dispatch, getState) => {
    const id = getId(messageId);
    const cleanEmoji = String(emoji || '').trim();
    if (!id || id.startsWith('local:') || !cleanEmoji) return null;

    const meId = getId(getState().auth?.user);
    const oldMessage = (getState().message?.messages || []).find((item) => getId(item?._id) === id);
    const previous = normalizeReactions(oldMessage?.reactions);
    if (meId) {
        dispatch({
            type: 'MESSAGE_REACTIONS_PATCHED',
            payload: { messageId: id, reactions: applyMyReaction(previous, meId, cleanEmoji) },
        });
    }

    try {
        const { data } = await axios.post(`/messages/${encodeURIComponent(id)}/reaction`, { emoji: cleanEmoji });
        const reactions = normalizeReactions(data?.reactions ?? data?.message?.reactions ?? []);
        dispatch({ type: 'MESSAGE_REACTIONS_PATCHED', payload: { messageId: id, reactions } });
        return data || { reactions };
    } catch (error) {
        dispatch({ type: 'MESSAGE_REACTIONS_PATCHED', payload: { messageId: id, reactions: previous } });
        throw error;
    }
};

export const requestVoiceTranscription = async (messageId) => {
    const id = getId(messageId);
    if (!id || id.startsWith('local:')) throw new Error('Не указан ID голосового сообщения');
    const { data } = await axios.post('/messages/voice/transcribe', { messageId: id });
    return data;
};

export const editMessageText = (messageId, text) => async (dispatch) => {
    const id = getId(messageId);
    if (!id || id.startsWith('local:')) throw new Error('Сообщение ещё не отправлено');
    const { data } = await axios.patch(`/messages/${encodeURIComponent(id)}`, { text });
    if (data?.message?._id) dispatch(receiveMessageUpdate(data.message));
    return data?.message || null;
};
